import { type ReactElement } from 'react'
import { TeamInlineSelect, TeamMultiInlineSelect } from 'app/components/pickers'
import { type CompanyId, isNotEmpty, type RoleId, type TeamId, visibleTeamMembershipRoles } from '@terros/common'
import { SimpleGrid, Stack } from '@mantine/core'
import { useContainerCols } from '@hooks'
import { Size, usePreference, useRoles, useTeams, useTranslation } from '@front/model'
import { type SelectedTeamMembership } from './types'
import { TeamRolesCard } from './TeamRolesCard'

type Props = {
  companyId?: CompanyId
  value: SelectedTeamMembership[]
  savedMemberships?: SelectedTeamMembership[]
  onChange: (memberships: SelectedTeamMembership[]) => void
}

export const TeamMembershipSelect = ({
  companyId,
  value: selectedTeamMemberships,
  savedMemberships,
  onChange,
}: Props): ReactElement => {
  const { t } = useTranslation()
  const { data: canBeInMultipleTeams } = usePreference('canBeInMultipleTeams', companyId)
  const { data: roles = [] } = useRoles({ companyId })
  const { data: teams = [] } = useTeams({ companyId })
  const selectedTeamIds = selectedTeamMemberships.map((m) => m.teamId)
  const teamNameById = new Map(teams.map((t) => [t.teamId, t.name]))
  const teamCols = useContainerCols({ base: 1, xs: Math.min(selectedTeamMemberships.length, 2) })
  const membershipRoles = new Map(
    selectedTeamMemberships.map((m) => [m.teamId, visibleTeamMembershipRoles(roles, m.roleIds)])
  )
  const hasVisibleRoles = [...membershipRoles.values()].some(isNotEmpty)

  const setTeamIds = (teamIds: TeamId[]): void => {
    const existing = new Map(selectedTeamMemberships.map((m) => [m.teamId, m.roleIds]))
    const saved = new Map((savedMemberships ?? []).map((m) => [m.teamId, m.roleIds]))
    onChange(teamIds.map((teamId) => ({ teamId, roleIds: existing.get(teamId) ?? saved.get(teamId) ?? [] })))
  }

  const toggleRole = (teamId: TeamId, roleId: RoleId, checked: boolean): void => {
    onChange(
      selectedTeamMemberships.map((m): SelectedTeamMembership => {
        if (m.teamId !== teamId) return m
        return {
          teamId,
          roleIds: checked ? [...m.roleIds, roleId] : m.roleIds.filter((r) => r !== roleId),
        }
      })
    )
  }

  return (
    <Stack gap={Size.space['2xl']}>
      {canBeInMultipleTeams ? (
        <TeamMultiInlineSelect
          label={t('Teams')}
          placeholder={t('Select teams')}
          companyId={companyId}
          value={selectedTeamIds}
          onChange={setTeamIds}
        />
      ) : (
        <TeamInlineSelect
          allowDeselect
          label={t('Team')}
          placeholder={t('Select team')}
          companyId={companyId}
          value={selectedTeamIds[0]}
          onChange={(teamId) => setTeamIds(teamId ? [teamId] : [])}
        />
      )}

      {hasVisibleRoles && isNotEmpty(selectedTeamMemberships) && (
        <SimpleGrid cols={teamCols} type='container'>
          {selectedTeamMemberships.map((membership) => (
            <TeamRolesCard
              key={membership.teamId}
              roles={membershipRoles.get(membership.teamId) ?? []}
              selectedRoleIds={membership.roleIds}
              teamName={teamNameById.get(membership.teamId) ?? membership.teamId}
              onToggleRole={(roleId, checked) => toggleRole(membership.teamId, roleId, checked)}
            />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  )
}
